import type { SkillInvocationListItem } from '../../lib/api-client';

import { StatusBadge, formatDuration, relativeTime, truncate } from './skill-invocation-helpers';

interface Props {
  invocations: SkillInvocationListItem[];
  status: 'idle' | 'loading' | 'success' | 'error';
  error?: string | undefined;
  hasMore: boolean;
  onLoadMore: () => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

/**
 * Left pane of the Skill Execution History tab. Rows are clickable and the
 * one matching `selectedId` is highlighted. Pagination via "Load more".
 */
export function SkillExecutionList({
  invocations,
  status,
  error,
  hasMore,
  onLoadMore,
  selectedId,
  onSelect,
}: Props) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        background: 'var(--bg-alt)',
      }}
    >
      <div
        style={{
          padding: '12px 16px',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.5,
          color: 'var(--text-mute)',
          textTransform: 'uppercase',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        Executions
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {status === 'loading' && invocations.length === 0 && (
          <Empty>Loading executions…</Empty>
        )}
        {status === 'error' && <Empty error>{error ?? 'Failed to load executions.'}</Empty>}
        {status === 'success' && invocations.length === 0 && (
          <Empty>No executions yet.</Empty>
        )}

        {invocations.map((inv) => {
          const isSelected = selectedId === inv.id;
          return (
            <button
              key={inv.id}
              type="button"
              onClick={() => onSelect(inv.id)}
              style={{
                display: 'block',
                width: '100%',
                padding: '10px 16px',
                background: isSelected ? 'var(--surface)' : 'transparent',
                borderLeft: isSelected ? '3px solid var(--accent)' : '3px solid transparent',
                borderBottom: '1px solid var(--border)',
                cursor: 'pointer',
                textAlign: 'left',
                color: 'var(--text)',
                fontFamily: 'inherit',
                fontSize: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <StatusBadge status={inv.status} />
                <span style={{ color: 'var(--text-mute)', fontSize: 11 }}>
                  {relativeTime(inv.started_at)}
                </span>
                <span style={{ flex: 1 }} />
                <span style={{ color: 'var(--text-mute)', fontSize: 11 }}>
                  {formatDuration(inv.duration_ms)}
                </span>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {inv.user_name ?? inv.user_email ?? inv.user_id}
              </div>
              {inv.conversation_title && (
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--text-mute)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  in “{truncate(inv.conversation_title, 60)}”
                </div>
              )}
            </button>
          );
        })}

        {hasMore && (
          <div style={{ padding: 12, textAlign: 'center' }}>
            <button
              type="button"
              onClick={onLoadMore}
              style={{
                padding: '6px 14px',
                fontSize: 12,
                border: '1px solid var(--border-strong)',
                borderRadius: 6,
                background: 'var(--surface)',
                color: 'var(--text)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Load more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Empty({ children, error }: { children: React.ReactNode; error?: boolean }) {
  return (
    <div
      style={{
        padding: 16,
        fontSize: 12,
        color: error ? 'var(--kind-failure)' : 'var(--text-mute)',
      }}
    >
      {children}
    </div>
  );
}
